// /app/frontend/src/components/frugal-v2/components/MonthSummaryModal.jsx
// Month Summary Modal with Accounts Section (< 250 lines)

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, DollarSign, PiggyBank, Shield, Save, Loader2, Download, TrendingUp, AlertTriangle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import ReportSection from './reports/ReportSection';
import AccountsBlock from './reports/AccountsBlock';
import { PreviousMonthCompareSection, Accumulated12mSection } from './reports/ComparisonSections';
import { translations, categoryNames } from './reports/reportTranslations';
import { generatePDF } from './reports/generatePDF';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MonthSummaryModal({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const t = translations[lang];
  const catNames = categoryNames[lang];

  useEffect(() => {
    if (isOpen) loadReport();
  }, [isOpen]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v2/frugal/reports/monthly-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setReport(await response.json());
      else setError(t.error);
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClick = () => setShowConfirm(true);

  const confirmSave = async () => {
    setShowConfirm(false);
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/v2/frugal/reports/save`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      response.ok ? toast.success(t.saved) : toast.error(t.error);
    } catch {
      toast.error(t.error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      await generatePDF(report, t, lang, catNames);
    } catch {
      toast.error(t.error);
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const accounts = report?.accounts || {};

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 rounded-2xl border border-white/10 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">{t.title}</h2>
          <div className="flex items-center gap-2">
            <button onClick={handleSaveClick} disabled={saving || !report}
              className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm rounded-lg">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t.saving : t.save}
            </button>
            <button onClick={handleDownload} disabled={downloading || !report}
              className="flex items-center gap-2 px-3 py-1.5 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-sm rounded-lg">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? t.downloading : t.download}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
              <span className="ml-3 text-slate-400">{t.loading}</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-400">{error}</div>
          ) : report && (
            <>
              {/* Income & Savings Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
                    <DollarSign className="w-4 h-4" /> {t.income}
                  </div>
                  <div className="text-2xl font-bold text-white">${report.income?.total?.toFixed(2)}</div>
                </div>
                <div className="bg-sky-500/10 border border-sky-500/20 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sky-400 text-sm mb-2">
                    <PiggyBank className="w-4 h-4" /> {t.savings}
                  </div>
                  <div className="text-2xl font-bold text-white">${report.savings?.total?.toFixed(2)}</div>
                </div>
              </div>

              {/* Accounts: Banks + Stats + Cards + Net Position */}
              <AccountsBlock accounts={accounts} t={t} />

              {/* Expenses by Category */}
              <ReportSection title={t.expenses} icon={TrendingUp}>
                <div className="space-y-2">
                  {report.expenses?.by_category?.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5">
                      <span className="text-white">{catNames[cat.category] || cat.category}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-slate-400 text-sm">{cat.percentage?.toFixed(1)}%</span>
                        <span className="text-white font-medium">${cat.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 font-semibold">
                    <span className="text-emerald-400">{t.totalExpenses}</span>
                    <span className="text-white">${report.expenses?.total?.toFixed(2)}</span>
                  </div>
                </div>
              </ReportSection>

              {/* Vital Analysis */}
              {report.vital_analysis?.length > 0 && (
                <ReportSection title={t.vitalAnalysis} icon={Shield}>
                  <div className="space-y-3">
                    {report.vital_analysis.map((v, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-3">
                        <div className="flex justify-between mb-2">
                          <span className="text-white font-medium">{catNames[v.category] || v.category}</span>
                          <span className={`text-sm ${v.consumption_percent > 100 ? 'text-red-400' : 'text-emerald-400'}`}>
                            {v.consumption_percent?.toFixed(1)}%
                          </span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full ${v.consumption_percent > 100 ? 'bg-red-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(v.consumption_percent, 100)}%` }} />
                        </div>
                        <div className="flex justify-between mt-1 text-xs text-slate-400">
                          <span>{t.budget}: ${v.budget?.toFixed(2)}</span>
                          <span>{t.spent}: ${v.spent?.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ReportSection>
              )}

              {/* Upcoming Scheduled Charges */}
              {report.scheduled_upcoming?.items?.length > 0 && (
                <ReportSection title={t.upcomingCharges} icon={Calendar}>
                  <div className="space-y-2">
                    {report.scheduled_upcoming.items.map((s, idx) => {
                      const dateStr = `${String(s.day).padStart(2, '0')}/${String(s.month).padStart(2, '0')}/${s.year}`;
                      return (
                        <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5" data-testid={`scheduled-${idx}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium truncate">{s.name}</span>
                              {s.annual && <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded whitespace-nowrap">{t.annual}</span>}
                            </div>
                            <div className="text-xs text-slate-400">{catNames[s.category] || s.category} · {dateStr}</div>
                          </div>
                          <span className="text-amber-400 font-medium ml-2">${s.amount?.toFixed(2)}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center justify-between pt-2 font-semibold">
                      <span className="text-amber-400">{t.totalUpcoming}</span>
                      <span className="text-white">${report.scheduled_upcoming.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </ReportSection>
              )}

              {/* Previous Month Comparison */}
              <PreviousMonthCompareSection data={report.previous_month_comparison} t={t} catNames={catNames} />

              {/* Accumulated 12 months by category */}
              <Accumulated12mSection data={report.accumulated_12m} t={t} catNames={catNames} />
            </>
          )}
        </div>

        {/* Save Confirmation Modal */}
        {showConfirm && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-6 z-10">
            <div className="bg-slate-800 rounded-xl p-6 max-w-md border border-amber-500/30">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
                <h3 className="text-lg font-semibold text-white">{t.confirmSaveTitle}</h3>
              </div>
              <p className="text-slate-300 mb-6">{t.confirmSaveMsg}</p>
              <div className="flex gap-3">
                <button onClick={() => setShowConfirm(false)}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">
                  {t.confirmSaveNo}
                </button>
                <button onClick={confirmSave}
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium">
                  {t.confirmSaveYes}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
